/**
 * WorkspaceDockview — the Dockview panel/tab/watermark renderers shared by
 * every one of RecruiterRoomPage's five sections (Video, Live Scoring,
 * Interview, Main Notes, Chat), plus the small per-section metadata
 * (id/title/icon) both that page and this file need. This is what replaced
 * the old fixed SplitPane tree (Pane/SplitPane, now deleted): dockview is a
 * real VS Code-style docking layout — every section can be dragged onto any
 * other to swap places or split beside/above/below it, resized from any edge
 * (not just its original two neighbors), closed via the × in its tab, and
 * reopened later from RecruiterRoomPage's header launcher.
 *
 * Kept at MODULE SCOPE (not defined inline inside RecruiterRoomPage) because
 * dockview-react's `components`/`defaultTabComponent`/`watermarkComponent`
 * props are only cheaply reactive when their identity is stable across
 * renders (see DockviewReact's internal prop-diff effect) — redefining these
 * functions on every parent render would make it think the renderer itself
 * changed on every keystroke.
 *
 * Icon is deliberately looked up from PANE_ICONS by the panel's own id
 * (a plain string, always available via `api.id`) rather than threaded
 * through as a param — a param survives this page's own localStorage
 * persistence (see RecruiterRoomPage's `api.toJSON()`/`fromJSON()`), and a
 * React component reference does NOT survive that JSON round-trip cleanly:
 * heroicons are `React.forwardRef` objects, and `JSON.stringify` silently
 * collapses one down to `{}` (every property is either a Symbol or a
 * function, both of which it drops) — restoring THAT as `params.icon` and
 * rendering `<Icon />` crashes with "Element type is invalid ... but got:
 * object". Deriving it from `id` instead sidesteps the hazard entirely: it
 * never needs to be serialized in the first place. `content`/`badge` don't
 * have this problem *for the icon's reason*, but they're still stripped from
 * what gets persisted (see RecruiterRoomPage) because they're live React
 * trees with closures — pointless (if not risky) to serialize when the
 * actual content is always re-pushed via `updateParameters` anyway.
 */
import React from 'react'
import {
  ChartBarIcon, ChatBubbleLeftRightIcon, DocumentTextIcon, PencilSquareIcon, PlayIcon, VideoCameraIcon, XMarkIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline'
import type { IDockviewPanelHeaderProps, IDockviewPanelProps, IWatermarkPanelProps } from 'dockview-react'

export type WorkspacePaneId = 'video' | 'interview' | 'live-scoring' | 'chat' | 'main-notes' | 'transcript' | 'integrity'

// Per-panel icon + title — shown in every dockview tab (via
// WorkspacePanelTab below) and in RecruiterRoomPage header's launcher (the
// row of icons that reopens a closed section). Kept to a single small glyph
// per section (no left-edge accent stripe, no per-panel colored icon chip):
// five sections of ONE workspace should read as one cohesive whole, not five
// independently branded cards.
export const PANE_ICONS: Record<WorkspacePaneId, React.ComponentType<{ className?: string }>> = {
  video: VideoCameraIcon,
  interview: PlayIcon,
  'live-scoring': ChartBarIcon,
  'main-notes': DocumentTextIcon,
  chat: ChatBubbleLeftRightIcon,
  transcript: PencilSquareIcon,
  integrity: ShieldExclamationIcon,
}
export const PANE_TITLES: Record<WorkspacePaneId, string> = {
  video: 'Video',
  interview: 'Interview',
  'live-scoring': 'Live Scoring',
  'main-notes': 'Main Notes',
  chat: 'Chat',
  transcript: 'Live Transcript',
  integrity: 'Integrity',
}
// Launcher display order — deliberately NOT alphabetical/id order, so the
// icon row reads Video, Interview, Live Scoring, Main Notes, Chat, Live
// Transcript regardless of what order sections happen to have been
// closed/reopened in. Transcript is last/closed-by-default (see
// RecruiterRoomPage's buildDefaultDockLayout) — reachable from here the
// same way every other closed-by-default section already is.
// Integrity (live alerts from the candidate's browser - see
// RecruiterRoomPage's 'integrity-event' handling) sits right after Chat.
export const PANE_ORDER: WorkspacePaneId[] = ['video', 'interview', 'live-scoring', 'main-notes', 'chat', 'integrity', 'transcript']

export interface WorkspacePanelParams {
  content: React.ReactNode
  badge?: React.ReactNode
}

export function WorkspacePanelContent({ params }: IDockviewPanelProps<WorkspacePanelParams>) {
  // overflow-hidden is the backstop that keeps a panel's content from ever
  // leaking past its allotted box — dockview hands each panel an EXACT
  // pixel size, and content genuinely taller/wider than that should scroll
  // WITHIN its own body (each body div below already opts into that via
  // overflow-y-auto), not escape upward into the grid and inflate the
  // whole page.
  return <div className="h-full w-full flex flex-col min-h-0 min-w-0 bg-white overflow-hidden">{params.content}</div>
}

// Mirrors dockview-react's own built-in DockviewDefaultTab structure/class
// names (`.dv-default-tab` / `-content` / `-action`) so it inherits that
// library's hover/active/dragging CSS for free — just with an icon and an
// optional badge (Chat's unread count) added in, in place of a bare title.
//
// The onPointerDown/onPointerUp/onPointerLeave props dockview hands every
// tab renderer are how it wires up tab drag-and-drop/reordering/dragging out
// into a floating window — they MUST land on this component's root element
// (see the built-in tab's own implementation) or dragging silently breaks.
type WorkspaceTabProps = IDockviewPanelHeaderProps<WorkspacePanelParams> & React.HTMLAttributes<HTMLDivElement>

export function WorkspacePanelTab({ api, params, onPointerDown, onPointerUp, onPointerLeave }: WorkspaceTabProps) {
  const Icon = PANE_ICONS[api.id as WorkspacePaneId]
  const title = api.title || ''
  return (
    <div
      className="dv-default-tab"
      // Guided-tour spotlight anchor (RecruiterRoomPage's RecruiterInterviewTour)
      // — a tab's OWN header, unlike its content, is always rendered regardless
      // of which tab in its group is currently active, which is what makes it
      // a reliable target for panels tabbed together (Interview/Live Scoring).
      data-tour={`dock-tab-${api.id}`}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerLeave}
    >
      {Icon && <Icon className="w-3.5 h-3.5 text-purple-600 flex-shrink-0 mr-1.5" />}
      <span className="dv-default-tab-content">{title}</span>
      {params.badge}
      <button
        type="button"
        className="dv-default-tab-action"
        aria-label={`Close ${title}`}
        // preventDefault (not the close) on pointerdown, exactly like the
        // built-in tab — this is what stops a click on the × from also
        // starting a tab drag.
        onPointerDown={(event) => event.preventDefault()}
        onClick={(event) => {
          event.preventDefault()
          api.close()
        }}
      >
        <XMarkIcon className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

// Shown in any grid cell that ends up with zero panels — a closed section,
// or (briefly, mid-drag) a group emptied by dragging its last tab out.
// Points back at the header launcher rather than leaving a blank rectangle.
export function WorkspaceWatermark(_props: IWatermarkPanelProps) {
  return (
    <div className="h-full w-full flex items-center justify-center text-center px-4 text-xs text-gray-400 bg-gray-50">
      No sections open here — use the icons in the header above to add one back.
    </div>
  )
}
